import React from "react";
import { MoreHorizontal } from "react-feather";
import { getWorkspaceInitials } from "../../atoms/WorkspaceInitialsBadge";
import {
  Card,
  CardBody,
  Description,
  Footer,
  Header,
  ImportantIcon,
  KindPill,
  MagicBiLogo,
  MenuButton,
  Owner,
  OwnerAvatar,
  OwnerInitials,
  OwnerName,
  PickerPreview,
  Preview,
  PreviewBar,
  PreviewCell,
  PreviewRow,
  PreviewRows,
  PreviewStack,
  RecipePreviewBar,
  RecipePreviewCell,
  RecipePreviewColumn,
  RecipePreviewHero,
  RecipePreviewRow,
  RecipePreviewSection,
  RecipePreviewStack,
  StoryCount,
  StoryCountIcon,
  StoryCountLabel,
  Title,
} from "./StoryTemplateCard.styles";
import type { StoryTemplateCardProps } from "./StoryTemplateCard.types";

const TemplatePreview: React.FC<Pick<StoryTemplateCardProps, "kind">> = ({
  kind,
}) =>
  kind === "system" ? (
    <Preview aria-hidden="true" data-testid="template-preview" data-kind={kind}>
      <PreviewBar $accent />
      <PreviewRows>
        <PreviewRow $height="large">
          <PreviewCell $width="wide" />
          <PreviewStack>
            <PreviewCell />
            <PreviewCell $accent />
          </PreviewStack>
        </PreviewRow>
        <PreviewRow>
          <PreviewCell $width="wide" />
          <PreviewCell $width="narrow" />
          <PreviewCell $accent $width="narrow" />
        </PreviewRow>
      </PreviewRows>
    </Preview>
  ) : (
    <Preview aria-hidden="true" data-testid="template-preview" data-kind={kind}>
      <PreviewBar />
      <PreviewRows>
        <PreviewRow>
          <PreviewCell $width="wide" />
          <PreviewCell $accent />
        </PreviewRow>
        <PreviewRow $height="compact">
          <PreviewCell />
          <PreviewCell />
          <PreviewCell />
        </PreviewRow>
        <PreviewBar $accent />
      </PreviewRows>
    </Preview>
  );

const PickerTemplatePreview: React.FC<
  Pick<StoryTemplateCardProps, "kind" | "pickerPreview">
> = ({ kind, pickerPreview }) => {
  const preview =
    pickerPreview ??
    (kind === "system" ? "executive" : kind === "custom" ? "marketing" : "sales");

  return (
    <PickerPreview
      aria-hidden="true"
      data-testid="template-preview"
      data-kind={kind}
      data-preview={preview}
    >
      {preview === "sales" && (
        <RecipePreviewStack>
          <RecipePreviewBar />
          <RecipePreviewRow $height="thirty">
            <RecipePreviewCell />
            <RecipePreviewCell $accent $narrow />
          </RecipePreviewRow>
          <RecipePreviewRow $height="fourteen">
            <RecipePreviewCell />
            <RecipePreviewCell />
            <RecipePreviewCell />
          </RecipePreviewRow>
          <RecipePreviewBar $accent />
        </RecipePreviewStack>
      )}
      {preview === "executive" && (
        <RecipePreviewStack>
          <RecipePreviewBar />
          {[0, 1, 2].map((row) => (
            <RecipePreviewRow key={row} $height="fourteen">
              <RecipePreviewCell />
              <RecipePreviewCell $accent $narrow />
            </RecipePreviewRow>
          ))}
        </RecipePreviewStack>
      )}
      {preview === "marketing" && (
        <RecipePreviewStack $narrow>
          <RecipePreviewBar $height="five" />
          <RecipePreviewBar $height="five" />
          <RecipePreviewHero />
          <RecipePreviewBar $height="five" />
          <RecipePreviewBar $height="five" />
        </RecipePreviewStack>
      )}
      {preview === "board" && (
        <RecipePreviewStack>
          {[0, 1].map((section) => (
            <RecipePreviewSection key={section}>
              <RecipePreviewBar $accent />
              <RecipePreviewRow $height="sixteen">
                <RecipePreviewCell />
                <RecipePreviewCell $narrow />
              </RecipePreviewRow>
            </RecipePreviewSection>
          ))}
        </RecipePreviewStack>
      )}
      {preview === "financial" && (
        <RecipePreviewStack>
          <RecipePreviewBar $accent $height="ten" />
          <RecipePreviewRow $height="thirty">
            <RecipePreviewCell />
            <RecipePreviewColumn>
              <RecipePreviewCell />
              <RecipePreviewCell $accent />
            </RecipePreviewColumn>
          </RecipePreviewRow>
          <RecipePreviewRow $height="fourteen">
            <RecipePreviewCell />
            <RecipePreviewCell />
            <RecipePreviewCell $accent />
          </RecipePreviewRow>
        </RecipePreviewStack>
      )}
    </PickerPreview>
  );
};

const KIND_LABEL: Record<StoryTemplateCardProps["kind"], string> = {
  system: "System",
  custom: "Custom",
  imported: "Imported",
};

export const StoryTemplateCard: React.FC<StoryTemplateCardProps> = ({
  title,
  description,
  kind,
  presentation = "catalog",
  pickerPreview,
  important = false,
  isFavorite = false,
  selected,
  storyCount,
  owner,
  onClick,
  onMenuClick,
  menuButtonRef,
  menuExpanded,
  className,
}) => {
  const isPicker = presentation === "picker";
  const interactiveProps = onClick
    ? {
        role: "button" as const,
        "aria-label": `Open ${title}`,
        "aria-pressed": selected,
        tabIndex: 0,
        onClick,
        onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick();
          }
        },
      }
    : {};

  return (
    <Card
      className={className}
      $interactive={Boolean(onClick)}
      $presentation={presentation}
      {...interactiveProps}
    >
      <CardBody $presentation={presentation}>
        <Header>
          {(important || isFavorite) && (
            <ImportantIcon
              $presentation={presentation}
              src="/story-template-card/important-star.svg"
              alt={isFavorite ? "Favourited" : ""}
              aria-hidden="true"
              data-testid={
                isFavorite ? "favorite-marker" : "important-marker"
              }
            />
          )}
          <Title $presentation={presentation} title={title}>
            {title}
          </Title>
          {!isPicker && (
            <KindPill
              $kind={kind}
              variant="neutral"
              size="md"
              showDot={false}
            >
              {KIND_LABEL[kind]}
            </KindPill>
          )}
          {!isPicker && (
            <MenuButton
              ref={menuButtonRef}
              type="button"
              aria-label={`More options for ${title}`}
              aria-haspopup="menu"
              aria-expanded={menuExpanded}
              onClick={(event) => {
                event.stopPropagation();
                onMenuClick?.();
              }}
            >
              <MoreHorizontal size={16} aria-hidden="true" />
            </MenuButton>
          )}
        </Header>

        {isPicker ? (
          <PickerTemplatePreview kind={kind} pickerPreview={pickerPreview} />
        ) : (
          <TemplatePreview kind={kind} />
        )}
        {!isPicker && (
          <Description title={description}>{description}</Description>
        )}
      </CardBody>

      {!isPicker && (
        <Footer>
          <Owner>
            {kind === "system" ? (
              <MagicBiLogo src="/logos/magicbi.svg" alt="" aria-hidden="true" />
            ) : owner.avatarUrl ? (
              <OwnerAvatar
                src={owner.avatarUrl}
                alt={owner.name}
                loading="lazy"
              />
            ) : (
              <OwnerInitials aria-label={owner.name}>
                {getWorkspaceInitials(owner.name)}
              </OwnerInitials>
            )}
            <OwnerName title={owner.name}>{owner.name}</OwnerName>
          </Owner>

          <StoryCount aria-label={`${storyCount} stories`}>
            <StoryCountIcon
              src="/story-template-card/sparkle.svg"
              alt=""
              aria-hidden="true"
            />
            {storyCount}
            <StoryCountLabel>stories</StoryCountLabel>
          </StoryCount>
        </Footer>
      )}
    </Card>
  );
};

export default StoryTemplateCard;
